import { Modal, Button, Form, message } from 'antd';
import { useCreateCliente } from '../api/useClientes';
import { useT } from '../../../locales';
import { ClienteForm, type ClienteFormValues } from './ClienteForm';
import type { CreateClienteDto } from '@aid/shared';
import { getClienteMutationErrorReason } from '../api/clientes-error';

interface ClienteCreateModalProps {
  open: boolean;
  onClose: () => void;
}

export function ClienteCreateModal({ open, onClose }: ClienteCreateModalProps) {
  const t = useT();
  const [form] = Form.useForm<ClienteFormValues>();
  const createCliente = useCreateCliente();

  const handleSubmit = async (values: CreateClienteDto) => {
    try {
      await createCliente.mutateAsync(values);
      message.success(t('pages.clientes.createSuccess'));
      onClose();
    } catch (error: unknown) {
      const reason = getClienteMutationErrorReason(error, t);
      message.error(
        reason
          ? t('pages.clientes.createValidationError', { reason })
          : t('pages.clientes.createError'),
      );
    }
  };

  return (
    <Modal
      title={t('pages.clientes.createTitle')}
      open={open}
      onCancel={onClose}
      destroyOnHidden
      centered
      width={640}
      styles={{ body: { maxHeight: 'min(75vh, 675px)', overflowY: 'auto' } }}
      footer={[
        <Button key="cancel" onClick={onClose}>
          {t('common.cancel')}
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={createCliente.isPending}
          onClick={() => form.submit()}
        >
          {t('pages.clientes.create')}
        </Button>,
      ]}
    >
      <ClienteForm
        mode="create"
        form={form}
        hideSubmit
        onSubmit={handleSubmit}
        loading={createCliente.isPending}
      />
    </Modal>
  );
}
